import React from 'react';
import { ClinicalDrug } from '../types/clinical';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';

interface ClinicalDrugDetailProps {
  cd: ClinicalDrug | null;
  onBack: () => void;
  onSelectProduct: (id: number) => void;
  ScoreRing: React.ComponentType<{ score: number; size?: number; strokeWidth?: number; color?: string; label?: string }>;
  RiskBadge: React.ComponentType<{ risk: string }>;
  IconArrowLeft: React.ComponentType;
  IconChevron: React.ComponentType;
  IconFlag: React.ComponentType;
  IconGlobe: React.ComponentType;
  IconCheck: React.ComponentType;
}

export default function ClinicalDrugDetailPage({
  cd,
  onBack,
  onSelectProduct,
  ScoreRing,
  RiskBadge,
  IconArrowLeft,
  IconChevron,
  IconFlag,
  IconGlobe,
  IconCheck,
}: ClinicalDrugDetailProps) {
  const { T } = useTheme();
  const { isMobile, isTablet } = useResponsive();
  if (!cd) return null;

  const totalPackagings = cd.products.reduce((sum, p) => sum + (p.packaging?.length || 0), 0);
  const allPackagings = cd.products.flatMap(p =>
    (p.packaging || []).map(pkg => ({ ...pkg, product: p }))
  ).sort((a, b) => (a.nadac_per_unit || Infinity) - (b.nadac_per_unit || Infinity));

  const lowestPrice = allPackagings.find(p => p.nadac_per_unit)?.nadac_per_unit;
  const highestPrice = allPackagings.filter(p => p.nadac_per_unit).pop()?.nadac_per_unit;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: T.navy, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 0, width: "fit-content" }}>
        <IconArrowLeft /> Back to Products
      </button>

      {/* Clinical Drug Header */}
      <div style={{ background: `linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)`, border: "1px solid #C4B5FD", borderRadius: 16, padding: isMobile ? "20px 16px" : "32px 36px", boxShadow: T.shadowMd, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, #7C3AED, #A855F7, #C084FC)` }} />
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <span style={{ fontSize: 28 }}>🧬</span>
          <h1 style={{ margin: 0, fontSize: isMobile ? 20 : 26, fontWeight: 800, color: "#4C1D95" }}>{cd.name}</h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#7C3AED", fontSize: 12 }}>
          <span>RxCUI: {cd.clinical_drug_id}</span>
          {cd.stats?.total_products !== undefined && <span>• {cd.stats.total_products} products</span>}
          {cd.stats?.total_packagings !== undefined && <span>• {cd.stats.total_packagings} packagings</span>}
        </div>
      </div>

      {/* Stats */}
      {cd.stats && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 16 }}>
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 18, boxShadow: T.shadow }}>
            <div style={{ fontSize: 10, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>Total Products</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.navy }}>{cd.stats.total_products}</div>
          </div>
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 18, boxShadow: T.shadow }}>
            <div style={{ fontSize: 10, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>Total Packagings</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.navy }}>{cd.stats.total_packagings}</div>
          </div>
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 18, boxShadow: T.shadow }}>
            <div style={{ fontSize: 10, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>Lowest NADAC</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.success }}>{cd.stats.lowest_nadac ? `$${cd.stats.lowest_nadac.toFixed(4)}` : "-"}</div>
          </div>
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 18, boxShadow: T.shadow }}>
            <div style={{ fontSize: 10, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 8 }}>Highest NADAC</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.danger }}>{cd.stats.highest_nadac ? `$${cd.stats.highest_nadac.toFixed(4)}` : "-"}</div>
          </div>
        </div>
      )}

      {/* Overview cards */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr", gap: 16 }}>
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, boxShadow: T.shadow }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>Pricing Summary</h3>
            {lowestPrice !== undefined && (
              <span style={{ fontSize: 11, color: T.success, fontWeight: 700 }}>Lowest: ${lowestPrice.toFixed(4)}</span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <ScoreRing score={Math.min(100, Math.round((cd.stats?.avg_nadac || 0) * 4))} size={70} strokeWidth={6} color={T.success} label="Avg NADAC" />
            <div>
              <div style={{ fontSize: 12, color: T.textLight }}>Average NADAC Price</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.textDark }}>
                {cd.stats?.avg_nadac ? `$${cd.stats.avg_nadac.toFixed(4)}` : '-'}
              </div>
              {highestPrice && (
                <div style={{ fontSize: 11, color: T.textLight }}>Highest: ${highestPrice.toFixed(4)}</div>
              )}
            </div>
          </div>
        </div>

        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, boxShadow: T.shadow }}>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>Compliance Summary</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 12, color: T.textMid }}>BAA Eligible</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.success }}>{cd.stats?.baa_eligible_count ?? 0}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 12, color: T.textMid }}>TAA Eligible</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.success }}>{cd.stats?.taa_eligible_count ?? 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Products list */}
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden", boxShadow: T.shadow }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, background: T.offWhite, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Products ({cd.products.length})
          </span>
          <span style={{ fontSize: 11, color: T.textLight }}>{totalPackagings} packagings</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {cd.products.map((p, i) => (
            <div
              key={p.id}
              onClick={() => onSelectProduct(p.id)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "14px 20px",
                cursor: "pointer",
                borderBottom: i < cd.products.length - 1 ? `1px solid ${T.borderLight}` : "none",
                background: T.white,
                transition: "background 0.15s"
              }}
              onMouseEnter={e => e.currentTarget.style.background = T.offWhite}
              onMouseLeave={e => e.currentTarget.style.background = T.white}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.product_name}
                </div>
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>
                  {p.manufacturer || "Unknown"} • NDC: {p.ndc_code}
                  {p.strength && <span> • {p.strength}</span>}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {p.api_source_country && (
                  <span style={{
                    padding: "3px 8px", borderRadius: 10, fontSize: 10, fontWeight: 600,
                    background: p.api_source_country === "United States" ? T.successBg : p.api_source_country === "China" ? T.dangerBg : T.warningBg,
                    color: p.api_source_country === "United States" ? T.success : p.api_source_country === "China" ? T.danger : T.warning
                  }}>
                    {p.api_source_country}
                  </span>
                )}
                <span style={{ color: T.textLight }}><IconChevron /></span>
              </div>
            </div>
          ))}
          {cd.products.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: T.textLight }}>
              No products found.
            </div>
          )}
        </div>
      </div>

      {/* Packaging Table */}
      {allPackagings.length > 0 && (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden", boxShadow: T.shadow }}>
          <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, background: T.offWhite }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Packagings ({allPackagings.length})
            </span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: T.offWhite }}>
                  {["Package NDC", "Description", "NADAC", "Product", "Manufacturer"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: T.textDark, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allPackagings.slice(0, 100).map((pkg, i) => (
                  <tr key={`${pkg.product?.id}-${pkg.package_ndc}-${i}`} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                    <td style={{ padding: "10px 14px", color: T.textMid }}>{pkg.package_ndc || '-'}</td>
                    <td style={{ padding: "10px 14px", color: T.textMid }}>{pkg.description || '-'}</td>
                    <td style={{ padding: "10px 14px", color: T.textMid }}>
                      {pkg.nadac_per_unit ? `$${pkg.nadac_per_unit.toFixed(4)}/${pkg.nadac_pricing_unit || 'unit'}` : '-'}
                    </td>
                    <td style={{ padding: "10px 14px", color: T.textMid }}>{pkg.product?.product_name || '-'}</td>
                    <td style={{ padding: "10px 14px", color: T.textMid }}>{pkg.product?.manufacturer || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {allPackagings.length > 100 && (
              <div style={{ padding: 12, textAlign: "center", color: T.textLight, fontSize: 11, background: T.offWhite }}>
                Showing 100 of {allPackagings.length} packagings
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
